import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_ENDPOINTS, axiosInstance } from "../../Api/apiConfig";

const DriverWeeklyReport = ({
  selectedDataId,
  reportStartDate,
  reportEndDate,
}) => {
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const { register, handleSubmit, setValue, reset, watch } = useForm();
  const [userData, setUserData] = useState([]);
  const [searchData, setSearchData] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = async () => {
    const loadno = searchTerm.trim().toLowerCase();

    if (!loadno) {
      toast.error("Please enter a Load # to search.");
      return;
    }

    const id = toast.loading("Searching for load...");
    let message = "Something went wrong. Please try again.";
    let type = "error";

    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.DRIVERWEEKLYREPORTSBYID,
        {
          params: { loadno },
        }
      );

      const result = response.data.message?.loads;

      if (!result || result.length === 0) {
        message = "No results found for the given Load #.";
      } else {
        // Update state for table rendering
        setSearchData(result);
        message = "Load found and displayed successfully.";
        type = "success";
      }
    } catch (error) {
      console.error("Search error:", error);
      message = error.response?.data?.message || "Search failed. Try again.";
    } finally {
      toast.update(id, {
        render: message,
        type: type,
        isLoading: false,
        autoClose: 2000,
      });
    }
  };

  async function get_user_account_details(selectedKey, startDatee, endDatee) {
    setIsLoading(true);
    try {
      let date = new Date(startDatee);
      const formattedDateStart = date.toISOString().split("T")[0];
      date = new Date(endDatee);
      const formattedDateEnd = date.toISOString().split("T")[0];
      const response = await axiosInstance.get(
        API_ENDPOINTS.DRIVERWEEKLYREPORTS,
        {
          params: {
            userName: selectedKey,
            startDate: formattedDateStart,
            endDate: formattedDateEnd,
          },
        }
      );
      const data = response.data.message.loads;
      setUserData(data);

      let totalGrandTotal = 0;

      data.forEach((item, index) => {
        setValue(`data[${index}].loadId`, item[0]);
        setValue(`data[${index}].label`, item[1]);
        setValue(`data[${index}].aalLoad`, item[2]);
        setValue(`data[${index}].shipperStartTime`, item[3]);
        if (item[4] === 1) {
          setValue(`data[${index}].isTonu`, "Yes");
        } else {
          setValue(`data[${index}].isTonu`, "No");
        }
        setValue(`data[${index}].detentionAmount`, item[5]);
        setValue(`data[${index}].lumperAmount`, item[6]);
        setValue(`data[${index}].baseRate`, item[7]);
        setValue(`data[${index}].driverName`, item[8]);
        setValue(`data[${index}].driver2Name`, item[9]);

        const totalRate = parseFloat(
          (
            parseFloat(item[5] || 0) +
            parseFloat(item[6] || 0) +
            parseFloat(item[7] || 0)
          ).toFixed(2)
        );
        setValue(`data[${index}].totalRate`, totalRate);

        totalGrandTotal = parseFloat((totalGrandTotal + totalRate).toFixed(2));
      });

      setValue("reportData.grandTotal", totalGrandTotal);

      const formatDate = (dateString) => {
        const date = new Date(dateString);
        const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // getUTCMonth() is zero-based, so we add 1
        const day = String(date.getUTCDate()).padStart(2, "0");
        return month + day;
      };

      const data2 = response.data.message.account[0];
      setValue(
        `reportData.reportNumber`,
        selectedDataId +
          " - " +
          formatDate(reportStartDate) +
          " - " +
          formatDate(reportEndDate)
      );
      setValue(`reportData.accountName`, data2.accountName);
      setValue(`reportData.accountNumber`, data2.accountDetails);

      setValue(`loanData[0].Detail`, data2.notes);
      setValue(`loanData[0].Amount`, data2.loan);

      setValue(`loanData[1].Detail`, "Loan Remaining");
    } catch (error) {
      console.error("Error getting userlist:", error);
    }
    setIsLoading(false);
  }

  const handleMergeSearchData = () => {
    if (!searchData.length) return;

    const newData = [...userData, ...searchData];
    setUserData(newData); // update userData state
    setSearchData([]); // clear the search table

    // Re-map the merged data into form fields
    let totalGrandTotal = 0;

    newData.forEach((item, index) => {
      setValue(`data[${index}].loadId`, item.loadId || item[0]);
      setValue(`data[${index}].label`, item.label || item[1]);
      setValue(`data[${index}].aalLoad`, item.aalLoad || item[2]);
      setValue(
        `data[${index}].shipperStartTime`,
        item.shipperStartTime || item[3]
      );

      const isTonuValue = item.isTonu !== undefined ? item.isTonu : item[4];
      setValue(`data[${index}].isTonu`, isTonuValue === 1 ? "Yes" : "No");

      const detention = parseFloat(item.detentionAmount ?? item[5] ?? 0);
      const lumper = parseFloat(item.lumperAmount ?? item[6] ?? 0);
      const base = parseFloat(item.baseRate ?? item[7] ?? 0);

      setValue(`data[${index}].detentionAmount`, detention);
      setValue(`data[${index}].lumperAmount`, lumper);
      setValue(`data[${index}].baseRate`, base);

      setValue(`data[${index}].driverName`, item.driverName || item[8] || "");
      setValue(`data[${index}].driver2Name`, item.driver2Name || item[9] || "");

      const total = parseFloat((detention + lumper + base).toFixed(2));
      setValue(`data[${index}].totalRate`, total);

      totalGrandTotal = parseFloat((totalGrandTotal + total).toFixed(2));
    });

    setValue("reportData.grandTotal", totalGrandTotal);
  };

  const deleteLoadRow = (index) => {
    const updatedUserData = [...userData];
    updatedUserData.splice(index, 1);
    setUserData(updatedUserData);
    reset((formValues) => {
      const newData = [...(formValues.data || [])];
      newData.splice(index, 1);
      let newTotal = 0;

      newData.forEach((item) => {
        const total_rate = parseFloat(item.totalRate || 0);
        newTotal += total_rate;
      });

      if (formValues.addData) {
        formValues.addData.forEach((item) => {
          newTotal += parseFloat(item.Amount || 0);
        });
      }

      return {
        ...formValues,
        data: newData,
        reportData: {
          ...formValues.reportData,
          grandTotal: parseFloat(newTotal.toFixed(2)),
        },
      };
    });
  };

  function extractContentBetweenBrackets(str) {
    const match = str.match(/\[(.*?)\]/);
    return match ? match[1] : null;
  }

  function extractWordAfterPeriod(str) {
    const parts = str.split(".");
    return parts.length > 1 ? parts[1] : null;
  }

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type === "change") {
        if (
          name &&
          name.substring(0, 4) === "data" &&
          ["detentionAmount", "lumperAmount", "baseRate"].includes(
            extractWordAfterPeriod(name)
          )
        ) {
          const extractedContent = extractContentBetweenBrackets(name);

          // Parse values or default to 0 if empty string
          const detentionAmount =
            parseFloat(value.data[extractedContent].detentionAmount) || 0;
          const lumperAmount =
            parseFloat(value.data[extractedContent].lumperAmount) || 0;
          const baseRate =
            parseFloat(value.data[extractedContent].baseRate) || 0;

          // Calculate totalRate
          const totalRate = detentionAmount + lumperAmount + baseRate;

          // Set value with setValue function
          setValue(`data[${extractedContent}].totalRate`, totalRate);
        }

        if (value && name) {
          // Calculate grandTotal
          let summedTotal = 0;
          for (let i = 0; i < value.data.length; i++) {
            summedTotal += parseFloat(value.data[i].totalRate) || 0;
          }

          for (let i = 0; i < value.addData.length; i++) {
            summedTotal += parseFloat(value.addData[i].Amount) || 0;
          }

          // Set grandTotal in reportData
          setValue("reportData.grandTotal", summedTotal);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (selectedDataId && reportStartDate && reportEndDate) {
      reset();
      get_user_account_details(selectedDataId, reportStartDate, reportEndDate);
    }
  }, [selectedDataId, reportStartDate, reportEndDate]);

  const onSubmit = async (data, addData, reportData) => {
    if (isRequestInProgress) {
      return;
    }
    setIsRequestInProgress(true);
    let id = toast.loading("Updating user details. Please wait...");
    try {
      let response = await axiosInstance.post(
        API_ENDPOINTS.DRIVERWEEKLYREPORTS,
        data,
        addData,
        reportData
      );
      toast.update(id, {
        render: "User finances updated successfully.",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
      document.getElementById("closeButtonEditFinances").click();
      const fileUrl = response.data.message[2];
      window.open(fileUrl, "_blank");
    } catch (error) {
      console.error("Error updating user finances:", error);
      toast.update(id, {
        render: error.response.data.message,
        type: "error",
        isLoading: false,
        autoClose: 1500,
      });
    } finally {
      setIsRequestInProgress(false);
    }
  };

  return (
    <div
      className="modal fade"
      id="modal3"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabIndex="-1"
      aria-labelledby="staticBackdropLabel"
      aria-hidden="true"
    >
      <div className="modal-xl modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content form-part-head">
          <div className="modal-header">
            <h1 className="modal-title fw-bold fs-5">
              Weekly Report for Driver: {selectedDataId}
            </h1>

            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              id="closeButtonEditFinances"
            ></button>
            <button
              type="submit"
              className="btn reg-user-submit"
              onClick={handleSubmit(onSubmit)}
            >
              Save Changes
            </button>
          </div>
          {!isLoading ? (
            <form onSubmit={handleSubmit(onSubmit)} className="modal-body">
              <div className="row mb-2">
                <div className="col-md-3 d-flex align-items-end">
                  <label className="col-form-label pb-0 mx-1">Report#</label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("reportData.reportNumber", { maxLength: 50 })}
                  />
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <label className="col-form-label pb-0 mx-1 single-line">
                    Name:
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("reportData.accountName", { maxLength: 50 })}
                  />
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <label className="col-form-label pb-0 mx-1 single-line">
                    Paid By:
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    {...register("reportData.accountNumber", { maxLength: 50 })}
                  />
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <label className="col-form-label pb-0 mx-1 single-line">
                    Grand:
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    {...register("reportData.grandTotal", { maxLength: 50 })}
                  />
                </div>
              </div>
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Start Time</th>
                    <th>Label</th>
                    <th>Load#</th>
                    <th>Tonu</th>
                    <th>Detention</th>
                    <th>Lumper</th>
                    <th>Base Rate</th>
                    <th>Total</th>
                    <th>Picker</th>
                    <th>Dropper</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {userData.map((item, index) => (
                    <tr key={item[0]}>
                      <td className="table-size-loadno">
                        <input
                          type="datetime-local"
                          {...register(`data[${index}].shipperStartTime`)}
                          readOnly
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          {...register(`data[${index}].label`)}
                        />
                      </td>
                      <td className="table-size-loadno">
                        <input
                          type="text"
                          {...register(`data[${index}].aalLoad`)}
                          readOnly
                        />
                      </td>

                      <td className="table-size-tonu">
                        <input
                          type="text"
                          {...register(`data[${index}].isTonu`)}
                          readOnly
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="number"
                          {...register(`data[${index}].detentionAmount`)}
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="number"
                          {...register(`data[${index}].lumperAmount`)}
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="number"
                          {...register(`data[${index}].baseRate`)}
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="number"
                          {...register(`data[${index}].totalRate`)}
                          readOnly
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="text"
                          {...register(`data[${index}].driverName`)}
                          readOnly
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="text"
                          {...register(`data[${index}].driver2Name`)}
                          readOnly
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link text-danger p-0"
                          onClick={() => deleteLoadRow(index)}
                          title="Delete Load"
                        >
                          <i
                            className="bi bi-x-circle-fill"
                            style={{ fontSize: "1.2rem" }}
                          ></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="my-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search load# to Add"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={handleSearch}
                  >
                    <i className="bi bi-search"></i> Search
                  </button>
                </div>
              </div>
              <table className="table table-hover">
                <tbody>
                  {searchData.map((item, index2) => (
                    <tr key={item.loadId}>
                      <td className="table-size-loadno">
                        <input
                          type="datetime-local"
                          defaultValue={
                            item.shipperStartTime?.slice(0, 16) || ""
                          }
                          disabled
                        />
                      </td>
                      <td>
                        <input type="text" defaultValue={item.label} disabled />
                      </td>
                      <td className="table-size-loadno">
                        <input
                          type="text"
                          defaultValue={item.aalLoad}
                          disabled
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="text"
                          defaultValue={item.isTonu === 1 ? "Yes" : "No"}
                          disabled
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="number"
                          defaultValue={item.detentionAmount}
                          disabled
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="text"
                          defaultValue={
                            item.lumperAmount && item.lumperAmount !== 0
                              ? `${item.lumperAmount} paid by ${
                                  item.lumperPaidBy || "N/A"
                                }`
                              : 0
                          }
                          disabled
                        />
                      </td>

                      <td className="table-size-tonu">
                        <input
                          type="number"
                          defaultValue={item.BaseRate}
                          disabled
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="number"
                          defaultValue={
                            (parseFloat(item.detentionAmount) || 0) +
                            (parseFloat(item.lumperAmount) || 0) +
                            (parseFloat(item.BaseRate) || 0)
                          }
                          disabled
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="text"
                          defaultValue={item.driverName}
                          disabled
                        />
                      </td>
                      <td className="table-size-tonu">
                        <input
                          type="text"
                          defaultValue={item.driver2Name}
                          disabled
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {/* Clear Search Button */}
                          <button
                            type="button"
                            className="btn btn-link text-danger p-0 me-2"
                            onClick={() => setSearchData([])}
                            title="Clear Search Results"
                          >
                            <i
                              className="bi bi-x-circle-fill"
                              style={{ fontSize: "1.2rem" }}
                            ></i>
                          </button>

                          {/* Move Up Button */}
                          <button
                            type="button"
                            className="btn btn-link text-primary p-0"
                            onClick={handleMergeSearchData}
                            title="Move Up"
                          >
                            <i
                              className="bi bi-arrow-up-circle-fill"
                              style={{ fontSize: "1.2rem" }}
                            ></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <table className="table table-hover table-responsive">
                <thead>
                  <tr>
                    <th>Detail</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr key={1}>
                    <td className="table-size-tonu">
                      <input
                        type="text"
                        {...register(`addData[${0}].Detail`)}
                      />
                    </td>
                    <td className="table-size-tonu">
                      <input
                        type="number"
                        defaultValue={0}
                        {...register(`addData[${0}].Amount`)}
                      />
                    </td>
                  </tr>
                  <tr key={2}>
                    <td className="table-size-tonu">
                      <input
                        type="text"
                        {...register(`addData[${1}].Detail`)}
                      />
                    </td>
                    <td className="table-size-tonu">
                      <input
                        type="number"
                        defaultValue={0}
                        {...register(`addData[${1}].Amount`)}
                      />
                    </td>
                  </tr>
                  <tr key={3}>
                    <td className="table-size-tonu">
                      <input
                        type="text"
                        {...register(`addData[${2}].Detail`)}
                      />
                    </td>
                    <td className="table-size-tonu">
                      <input
                        type="number"
                        defaultValue={0}
                        {...register(`addData[${2}].Amount`)}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className="table table-hover table-responsive">
                <thead>
                  <tr>
                    <th>Loan Detail</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr key={1}>
                    <td className="table-size-tonu">
                      <input
                        type="text"
                        {...register(`loanData[${0}].Detail`)}
                      />
                    </td>
                    <td className="table-size-tonu">
                      <input
                        type="number"
                        defaultValue={0}
                        {...register(`loanData[${0}].Amount`)}
                      />
                    </td>
                  </tr>
                  <tr key={2}>
                    <td className="table-size-tonu">
                      <input
                        type="text"
                        {...register(`loanData[${1}].Detail`)}
                      />
                    </td>
                    <td className="table-size-tonu">
                      <input
                        type="number"
                        defaultValue={0}
                        {...register(`loanData[${1}].Amount`)}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </form>
          ) : (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverWeeklyReport;
